import { Modal } from 'react-bootstrap';
import { Search, Info } from "lucide-react";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import PlatformIcon from "../PlatformIcon";
import '../style/platformTabs.scss';
import { usePlatforms } from '../../../contexts/PlatformsContext';
import { useKeywordSearchSWR } from '../../../hooks/useKeywordSearchSWR'
import { getLocalItem } from '../../../utils/storage'
import { api } from '../../../utils/variables'
import axios from 'axios'
import FilterPanel from '../../KeywordTrackingNewUIPage/FilterPanel'
import { useFilters } from '../../../hooks/useFilters'
import ConfirmRemoveTagModal from '../../Common/Modal/ConfirmRemoveTagModal'
import { useSearchProgress } from '../../../hooks/useSearchProgress';
import SearchProgressModal from '../Modal/SearchProgressModal';

// Icon + platform name row. PlatformIcon doesn't accept a style prop, so the
// spacing between the icon and the name lives on this wrapper.
const PLATFORM_LABEL_STYLE = { display: "inline-flex", alignItems: "center", gap: 6 };

// Pico's logo is a solid circle that fills its whole box (the others have
// built-in padding), so it reads larger than the name at the same height.
const getPlatformIconSize = (platformId) =>
  String(platformId || "").toLowerCase() === "pico" ? 14 : 16;

const KeywordTrackingDetailsModal = ({
  show,
  handleClose,
  title,
  searchTerm,
  onSearchChange,
  onSearchClick,
  results = [],
  loading = false,
  onSelectApp, // optional callback on "Select"
  // When true, shows "Select" instead of "Track" button
  showSelectButton = false,
  // Optional callback invoked when backend indicates an UPGRADE is required.
  // Signature: () => void
  onUpgrade,
  // Optional array of already tracked item ids (to disable/label them)
  trackedIds = [],
  // Fallback polling config (optional)
  // default: disabled to avoid automatic background API polling from the modal
  enableFallbackPolling = true,
  pollMaxAttempts = 5,
  pollInitialIntervalMs = 5000,
  pollDebug = false,
  // When provided, modal is opened to create this tag (string). If in create-mode and a search
  // has been submitted, tracked results will show View + Remove-from-tag actions.
  createTag = null,
  // Optional callback invoked when user clicks the Remove (trash) action for a tracked item.
  // Signature: async (itemId: string) => Promise<void>
  onRemoveFromTag,
  // Tags available for selection (array of { name, game_ids }) passed from parent
  tags = [],
  // Optional callback when user clicks a group label in the modal: (groupName) => void
  onOpenGroup,
  // Callback for bulk-adding multiple item ids to a tag: async (ids: string[], tagName: string) => Promise
  onBulkAddToTag,
  // Callback to add/move a single item to a tag: async (itemId: string, tagName: string) => Promise
  onAddToTag,
  // Optional initial platform to use for the first search (e.g. 'meta').
  // When provided, this will be used instead of the active tab for the first API call.
  initialPlatform = null,
  // When true, enables bulk selection and tracking features (App Tracking only)
  enableBulkActions = false,
  // Optional array of platform ids (e.g. ['steam', 'apple_vision']) to show as
  // disabled in the platform tabs, with a tooltip on hover — used by callers
  // whose search doesn't have data for every store platform (e.g. Ad Discovery,
  // whose ad-creative search only covers Meta today). Other callers of this
  // shared modal (e.g. App Tracking) leave this empty and keep all tabs active.
  disabledPlatformIds = [],
  disabledPlatformTooltip = "Coming soon",
}) => {
  const { getActivePlatforms, getPlatformDisplayName, loading: platformsLoading } = usePlatforms();
  const [modalLoading, setModalLoading] = useState(false)
  // Default to first active platform from API
  const [activeTab, setActiveTab] = useState(() => {
    const activePlatforms = getActivePlatforms();
    return activePlatforms.length > 0 ? activePlatforms[0].id : "meta";
  });
  const [showTrackedOnly, setShowTrackedOnly] = useState(false)
  // local input + submitted keyword to control when SWR should run
  const [localInput, setLocalInput] = useState(searchTerm || "")
  const [submittedKeyword, setSubmittedKeyword] = useState(null)
  const [pollAttempt, setPollAttempt] = useState(0)
  const { filterSections } = useFilters()
  const [showFilters, setShowFilters] = useState(false)
  const [modalFilters, setModalFilters] = useState({})
  // keep raw UI selection so FilterPanel can reopen with previous choices
  const [modalFiltersRaw, setModalFiltersRaw] = useState({})

  // Handler to remove individual filter
  const handleRemoveModalFilter = (key) => {
    const newFiltersRaw = { ...(modalFiltersRaw || {}) };
    if (Object.prototype.hasOwnProperty.call(newFiltersRaw, key)) {
      delete newFiltersRaw[key];
      setModalFiltersRaw(newFiltersRaw);

      // Convert to API format
      const apiFilters = {};
      if (newFiltersRaw) {
        if (newFiltersRaw.price) apiFilters.price = newFiltersRaw.price;
        if (newFiltersRaw.rating) apiFilters.rating = newFiltersRaw.rating;
        if (newFiltersRaw.downloads) apiFilters.downloads = newFiltersRaw.downloads;
        if (newFiltersRaw.revenue) apiFilters.revenue = newFiltersRaw.revenue;
        if (newFiltersRaw.release_date) apiFilters.release_date = newFiltersRaw.release_date;
        if (newFiltersRaw.genre) apiFilters.genre = newFiltersRaw.genre;
        if (newFiltersRaw.category) apiFilters.category = newFiltersRaw.category;
        if (newFiltersRaw.bundle) apiFilters.bundle = newFiltersRaw.bundle;
        if (newFiltersRaw.number_of_players) apiFilters.number_of_players = newFiltersRaw.number_of_players;
        if (newFiltersRaw.sort_by) apiFilters.sort_by = newFiltersRaw.sort_by;
        else if (newFiltersRaw.sort) apiFilters.sort_by = newFiltersRaw.sort;
      }

      setModalFilters(apiFilters);

      // If a search term is present, re-run the modal search with updated filters
      if (localInput && localInput.trim()) {
        setSubmittedKeyword(localInput.trim());
      }
    }
  };

  // Use SWR for modal searches. `intervalMs` set to 0 to disable automatic polling.
  // SWR will still perform an initial fetch when `submittedKeyword` is set.
  const swr = useKeywordSearchSWR({
    keyword: submittedKeyword,
    page: 1,
    platform: initialPlatform ? initialPlatform : (activeTab !== "all" ? activeTab : ""),
    filters: modalFilters,
    // When `enableFallbackPolling` is true we rely on the explicit fallback poller.
    // Otherwise allow SWR to auto-refresh while the backend reports `action: 'LOADING'`.
    intervalMs: enableFallbackPolling ? 0 : pollInitialIntervalMs,
  })
  const swrData = swr?.data
  // Decide what data to render: prefer SWR results when searching via modal.
  // Fall back to parent results if SWR hasn't returned yet, provided we have them.
  const hasParentResults = Array.isArray(results) && results.length > 0
  const effectiveResults = (swrData && swrData.games) ? swrData.games : results

  // Determine whether SWR or the fallback poller is currently working.
  let swrIsLoading = false
  if (!!submittedKeyword) {
    if (!swrData) {
      // If we have parent results, we don't consider SWR "loading" enough to show skeletons
      swrIsLoading = !hasParentResults
    } else if (swrData?.action === "LOADING") {
      swrIsLoading = true
    } else if (swrData?.action === "DISPLAY") {
      // Snappy loading: if we have games (from SWR or parent), don't show skeletons even if validating.
      // But if games array is empty, we show skeletons if we are still validating OR if we are in the middle of initial persistent polls.
      const hasAnyGames = (Array.isArray(swrData.games) && swrData.games.length > 0) || hasParentResults
      const isStillPolling = pollAttempt > 0 && pollAttempt < 3 && !hasAnyGames
      swrIsLoading = !!(swr.isLoading || (swr.isValidating && !hasAnyGames) || isStillPolling)
    } else {
      // Fallback: if no definitive action, show skeletons if we have no games and are still active.
      const hasAnyGames = (Array.isArray(swrData.games) && swrData.games.length > 0) || hasParentResults
      const isStillPolling = pollAttempt < 3 && !hasAnyGames
      swrIsLoading = !!(swr.isLoading || swr.isValidating || isStillPolling)
    }
  }

  const isLoadingResults = loading || swrIsLoading

  // Search progress modal state
  const { progress, showProgressModal, cancel: cancelSearchProgress } = useSearchProgress({
    loading: isLoadingResults,
  });

  // If server returns an UPGRADE response payload (e.g. { status: 400, action: 'UPGRADE' }),
  // notify parent via onUpgrade (if provided) and clear modal results to avoid showing stale data.
  useEffect(() => {
    if (!swrData) return
    const status = swrData?.status
    const action = swrData?.action || ""
    if (status === 400 && action === "UPGRADE") {
      // Reset submitted keyword to stop SWR polling for this keyword
      setSubmittedKeyword(null)
      // Notify parent to open upgrade modal (parent should also close this modal)
      try {
        if (typeof onUpgrade === "function") onUpgrade()
      } catch (err) {
        // ignore
      }
    }
  }, [swrData, onUpgrade])


  // Fallback polling: configurable, stops after `pollMaxAttempts` and supports exponential backoff.
  const pollTimerRef = useRef(null)
  const pollAttemptRef = useRef(0)
  const currentIntervalRef = useRef(pollInitialIntervalMs)
  const isPollingRef = useRef(false) // Track if polling is active to prevent multiple simultaneous polls

  // Clear individual poll timer
  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    isPollingRef.current = false
  }

  // Reset polling when keyword or filters change
  useEffect(() => {
    clearPoll()
    pollAttemptRef.current = 0
    setPollAttempt(0)
    currentIntervalRef.current = pollInitialIntervalMs
  }, [submittedKeyword, modalFilters, activeTab])

  useEffect(() => {
    // Do nothing unless user has submitted a keyword and fallback polling is enabled
    if (!submittedKeyword || !enableFallbackPolling) {
      clearPoll()
      return
    }

    // Prevent multiple simultaneous polling sessions
    if (isPollingRef.current) {
      return
    }

    // Clear any existing timer before starting
    clearPoll()
    pollAttemptRef.current = 0
    setPollAttempt(0)
    currentIntervalRef.current = pollInitialIntervalMs

    const schedulePoll = () => {
      // Safety: stop if we've reached max attempts
      if (pollAttemptRef.current >= pollMaxAttempts) {
        isPollingRef.current = false
        if (pollDebug)
          console.debug(
            "[KeywordModal] poll: reached maxAttempts",
            pollMaxAttempts
          )
        return
      }

      pollAttemptRef.current += 1
      setPollAttempt(pollAttemptRef.current)

      // Schedule next poll
      pollTimerRef.current = setTimeout(() => {
        // Check if we should continue polling
        const isStillLoading = swrData?.action === "LOADING"
        const noResultsYet = !swrData || !Array.isArray(swrData.games) || swrData.games.length === 0

        // Only continue if still loading or no results yet (and under 3 attempts)
        if (isStillLoading || (noResultsYet && pollAttemptRef.current < 3)) {
          // Trigger revalidation
          try {
            swr.mutate()
          } catch (err) {
            /* ignore */
          }

          // exponential backoff: double interval for next attempt
          currentIntervalRef.current = Math.min(
            currentIntervalRef.current * 2,
            60000
          ) // cap at 60s
          if (pollDebug)
            console.debug(
              "[KeywordModal] poll: scheduling next in",
              currentIntervalRef.current
            )
          schedulePoll()
        } else {
          isPollingRef.current = false
          if (pollDebug)
            console.debug(
              "[KeywordModal] poll: server reported data or max initial empty polls reached, stopping"
            )
        }
      }, currentIntervalRef.current)
    }

    // Start polling if server currently reports LOADING or if results are empty
    const isStillLoading = swrData?.action === "LOADING"
    const noResultsYet = !swrData || !Array.isArray(swrData.games) || swrData.games.length === 0

    if (isStillLoading || noResultsYet) {
      isPollingRef.current = true
      if (pollDebug)
        console.debug(
          "[KeywordModal] poll: starting fallback polling for",
          submittedKeyword
        )
      schedulePoll()
    }

    return () => {
      clearPoll()
    }
  }, [
    submittedKeyword,
    swrData?.action,
    enableFallbackPolling,
    pollMaxAttempts,
    pollInitialIntervalMs,
    pollDebug,
    // Removed swrData?.games?.length to prevent infinite loops
    // Only depend on action to determine if we need to poll
  ])
  // Filter results based on active tab (or tracked-only toggle).
  // Prefer SWR results when available.
  const filteredResults = useMemo(() => {
    // Show results when:
    // - modal-controlled search is active (`submittedKeyword` or `swrData`)
    // - OR parent opened modal with a `searchTerm` and provided `results` (use parent's results)
    if (!submittedKeyword && !swrData) {
      // Only use parent-provided results when the modal input is non-empty.
      // This prevents the parent `searchTerm` from forcing results to show
      // after the user explicitly cleared the modal input.
      if (
        localInput &&
        String(localInput).trim() &&
        searchTerm &&
        String(searchTerm).trim() &&
        Array.isArray(results) &&
        results.length > 0
      ) {
        // Apply same filtering logic to parent-provided results so the
        // "Tracked Apps" checkbox has an effect even when the parent
        // already performed the search and passed `results` in.
        const localStored = getLocalItem('comparisonBetween') || []
        const localTrackedIds = Array.isArray(localStored)
          ? localStored.map(g => g?.game_details?.item_id).filter(Boolean)
          : []
        const effectiveTrackedIds = trackedIds && trackedIds.length > 0 ? trackedIds : localTrackedIds

        const isTracked = game => {
          const id =
            game?.item_id ||
            game?._id ||
            game?.game_details?.item_id ||
            game?.game_details?._id ||
            null
          return effectiveTrackedIds && effectiveTrackedIds.length > 0 && id != null && effectiveTrackedIds.includes(id)
        }

        let filtered = results.slice()
        if (showTrackedOnly) {
          filtered = filtered.filter(isTracked)
        } else {
          filtered = filtered.filter(game => game.platform?.toLowerCase() === activeTab.toLowerCase())
        }

        return filtered
      }
      return []
    }

    const baseResults = effectiveResults || []
    if (baseResults && baseResults.length > 0) {
      // Determine tracked ids: prefer parent-provided prop, otherwise fall back to namespaced localStorage
      const localStored = getLocalItem('comparisonBetween') || []
      const localTrackedIds = Array.isArray(localStored)
        ? localStored.map(g => g?.game_details?.item_id).filter(Boolean)
        : []
      const effectiveTrackedIds = trackedIds && trackedIds.length > 0 ? trackedIds : localTrackedIds

      const isTracked = game => {
        // Games returned by different endpoints may carry the id under
        // different keys. Check common locations to robustly detect tracking.
        const id =
          game?.item_id ||
          game?._id ||
          game?.game_details?.item_id ||
          game?.game_details?._id ||
          null
        return effectiveTrackedIds && effectiveTrackedIds.length > 0 && id != null && effectiveTrackedIds.includes(id)
      }

      let filtered = baseResults.slice()

      // If user wants to see only tracked apps, ignore platform filter and show tracked across platforms
      if (showTrackedOnly) {
        filtered = filtered.filter(isTracked)
      } else {
        // Apply platform tab filter
        filtered = filtered.filter(
          game => game.platform?.toLowerCase() === activeTab.toLowerCase()
        )

        // If the user has an active modal search (submittedKeyword), show tracked apps first.
        if (submittedKeyword && submittedKeyword.trim()) {
          filtered = filtered.slice().sort((a, b) => {
            const aTracked = isTracked(a) ? 0 : 1
            const bTracked = isTracked(b) ? 0 : 1
            return aTracked - bTracked
          })
        } else {
          // Default behaviour: move already-tracked items to the end for clarity
          filtered = filtered.slice().sort((a, b) => {
            const aTracked = isTracked(a) ? 1 : 0
            const bTracked = isTracked(b) ? 1 : 0
            return aTracked - bTracked
          })
        }
      }

      return filtered
    } else {
      return []
    }
  }, [results, activeTab, swrData, trackedIds, submittedKeyword, showTrackedOnly, searchTerm, localInput, effectiveResults])

  // Reset local modal search state when modal closes
  useEffect(() => {
    if (!show) {
      // The backend keeps processing an abandoned search after the modal closes
      // (so the "we'll email you" flow can complete), and its result still lands
      // in the SWR cache for this exact keyword. Clear that cache entry now so a
      // later search for the same keyword always issues a fresh request instead
      // of silently resolving from stale/background-completed cache data.
      if (submittedKeyword) {
        try {
          swr.mutate(undefined, { revalidate: false })
        } catch (err) {
          // ignore
        }
      }
      // Clear common input/search state
      setLocalInput("")
      setSubmittedKeyword(null)
      // Clear modal filters and UI state
      setShowFilters(false)
      setModalFilters({})
      setModalFiltersRaw({})
      // Selection / bulk state
      setSelectedIds([])
      setBulkTagSelection("")
      setSelectedMemberTags({})
      setConfirmRemove(null)
      // Loading / drag state
      setRowLoadingMap({})
      setDraggedItemId(null)
      setDragSourceTag(null)
      // Move modal state removed
      setModalLoading(false)
      // Reset auto-submit flag
      hasAutoSubmittedRef.current = false

      // try to reset parent input if handler present (best-effort)
      if (onSearchChange) {
        try {
          onSearchChange({ target: { value: "" } })
        } catch (err) {
          // ignore
        }
      }
    }
  }, [show])

  // NOTE: Automatic submission of the parent's search term when opening the modal
  // has been intentionally removed to avoid triggering implicit network requests.
  // If you want to start a modal search on open, call `setSubmittedKeyword(...)`
  // from the parent or wire a prop to enable that behavior explicitly.

  // Keep local input in sync when parent searchTerm changes
  useEffect(() => {
    setLocalInput(searchTerm || "")
  }, [searchTerm])

  // When the user clears the modal input, clear any submitted keyword and
  // the displayed results so the modal shows an empty state.
  useEffect(() => {
    if (!localInput || !String(localInput).trim()) {
      // Cancel any in-flight modal search
      setSubmittedKeyword(null)
    }
  }, [localInput])

  // If the parent opened the modal with an existing `searchTerm`, auto-submit
  // it on open so the modal's first API call includes the platform parameter.
  // Use a ref to track if we've already auto-submitted to prevent repeated submissions
  const hasAutoSubmittedRef = useRef(false)
  const lastAutoSubmittedTermRef = useRef(null)

  useEffect(() => {
    // Reset auto-submit flag when modal closes
    if (!show) {
      hasAutoSubmittedRef.current = false
      lastAutoSubmittedTermRef.current = null
      return
    }

    // Get the trimmed search term
    const trimmedTerm = searchTerm && String(searchTerm).trim() ? String(searchTerm).trim() : null

    // Only auto-submit if:
    // 1. Modal is open
    // 2. We have a valid search term
    // 3. The search term is different from what we last auto-submitted
    // 4. We haven't already auto-submitted for this term
    if (
      show &&
      trimmedTerm &&
      trimmedTerm !== lastAutoSubmittedTermRef.current &&
      !hasAutoSubmittedRef.current
    ) {
      hasAutoSubmittedRef.current = true
      lastAutoSubmittedTermRef.current = trimmedTerm
      setSubmittedKeyword(trimmedTerm)
    }
  }, [show, searchTerm])

  // Ensure modal always defaults to the first active platform whenever it opens.
  // This resets transient tab selection from previous opens.
  useEffect(() => {
    if (show) {
      const activePlatforms = getActivePlatforms();
      if (activePlatforms.length > 0) {
        setActiveTab(activePlatforms[0].id);
      }
    }
  }, [show, getActivePlatforms])

  const handleTrackClick = async (game, addToTagAfter = null, rowId = null) => {
    // Always call the tracking API directly first to ensure the backend receives
    // the track request even if a parent handler does not perform the network call.
    const id = rowId || game?.item_id || game?._id
    const useRowLoading = !!id
    if (useRowLoading) setRowLoading(id, true)
    else setModalLoading(true)

    try {
      // 1) Prefer parent handler to perform tracking (so it can update local state/order).
      // If parent didn't provide a handler, fall back to calling the tracking API directly.
      if (typeof onSelectApp === 'function') {
        await Promise.resolve(onSelectApp(game))
      } else {
        try {
          const payload = { item_id: game?.item_id || game?._id }
          await axios.post(`${api}/tracker/tracking/app/`, payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
          })
        } catch (err) {
          console.error('tracking API failed', err)
          throw err
        }
      }

      // 2) If requested, call tag-add endpoint
      if (addToTagAfter) {
        try {
          // Prefer parent's handler if provided to avoid duplicate network calls.
          if (typeof onAddToTag === 'function') {
            await Promise.resolve(onAddToTag(id, addToTagAfter))
          } else {
            const Data = { item_id: id, tag_name: addToTagAfter, tag_type: 'APP_TRACKING' }
            await axios.post(`${api}/tracker/tags.keywords/`, Data, {
              headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
            })
          }
        } catch (err) {
          console.error('tag-add API failed', err)
          // If parent handler exists but threw, still rethrow so caller can handle
          throw err
        }

        // After successfully adding to the tag, close the modal so the
        // user returns to the page with the updated group membership.
        try {
          if (typeof handleClose === 'function') handleClose()
        } catch (err) {
          /* ignore */
        }
      }
    } finally {
      if (useRowLoading) setRowLoading(id, false)
      else setModalLoading(false)
    }
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
  }

  const effectiveTrackedIdsForRender = trackedIds || []

  const inCreateMode = !!createTag
  const showTrackedSearchActions = inCreateMode && submittedKeyword && submittedKeyword.trim()

  const [selectedIds, setSelectedIds] = useState([])
  const [bulkTagSelection, setBulkTagSelection] = useState("")
  const [selectedMemberTags, setSelectedMemberTags] = useState({})
  const [confirmRemove, setConfirmRemove] = useState(null) // { itemId, tagName, itemName }
  const [rowLoadingMap, setRowLoadingMap] = useState({})
  const [draggedItemId, setDraggedItemId] = useState(null)
  const [dragSourceTag, setDragSourceTag] = useState(null)
  // move modal state removed

  const toggleSelectId = id => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const setRowLoading = (id, value) => {
    setRowLoadingMap(prev => ({ ...(prev || {}), [id]: !!value }))
  }

  const handleDragStart = (e, itemId) => {
    try {
      e.dataTransfer.setData('text/plain', String(itemId))
    } catch (err) {
      // some browsers may restrict; still set state
    }
    setDraggedItemId(itemId)
    // compute source tag (first tag that contains this item)
    const source = Array.isArray(tags) ? tags.find(t => Array.isArray(t.game_ids) && t.game_ids.includes(itemId)) : null
    setDragSourceTag(source ? source.name : null)
  }

  const handleDragEnd = () => {
    setDraggedItemId(null)
    setDragSourceTag(null)
  }

  const handleDropOnTag = async (e, targetTagName) => {
    e.preventDefault()
    const raw = e.dataTransfer?.getData('text/plain')
    const itemId = raw || draggedItemId
    if (!itemId) return
    if (!targetTagName) return
    // If item already in target, nothing to do
    const alreadyInTarget = Array.isArray(tags) && tags.some(t => t.name === targetTagName && Array.isArray(t.game_ids) && t.game_ids.includes(itemId))
    if (alreadyInTarget) return

    try {
      setRowLoading(itemId, true)
      // 1) Add to target tag
      if (typeof onAddToTag === 'function') {
        await Promise.resolve(onAddToTag(itemId, targetTagName))
      } else {
        const Data = { item_id: itemId, tag_name: targetTagName, tag_type: 'APP_TRACKING' }
        await axios.post(`${api}/tracker/tags.keywords/`, Data, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } })
      }

      // 2) Remove from source tag if present and different
      const source = dragSourceTag
      if (source && source !== targetTagName) {
        if (typeof onRemoveFromTag === 'function') {
          await Promise.resolve(onRemoveFromTag(itemId, source))
        } else {
          // remove item from the source tag via PATCH (API used elsewhere in repo)
          await axios.patch(`${api}/tracker/tags.keywords/${encodeURIComponent(source)}/`, { item_id: itemId, tag_name: source }, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } })
        }
      }
    } catch (err) {
      console.error('drag/drop move failed', err)
    } finally {
      setRowLoading(itemId, false)
      setDraggedItemId(null)
      setDragSourceTag(null)
    }
  }

  const handleBulkAdd = async () => {
    if (!onBulkAddToTag || selectedIds.length === 0 || !bulkTagSelection) return
    try {
      setModalLoading(true)
      await onBulkAddToTag(selectedIds, bulkTagSelection)
      // clear selection after success
      setSelectedIds([])
      setBulkTagSelection("")
    } catch (err) {
      console.error('bulk add failed', err)
    } finally {
      setModalLoading(false)
    }
  }

  // Open the small "Move" modal used to pick a destination tag for a single item.
  // Move functionality removed: no-op placeholder kept for compatibility if called elsewhere
  // (openMoveModal / handleMoveToTag were intentionally removed)

  return (
    <>
      <SearchProgressModal
        show={showProgressModal}
        progress={progress}
        title="Searching for apps"
        onCancel={() => { cancelSearchProgress(); handleClose(); }}
        hasResults={filteredResults.length > 0}
      />
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        className="modal_key"
      >
        {title && (
          <Modal.Header closeButton>
            <Modal.Title>{title || "Select an App"}</Modal.Title>
          </Modal.Header>
        )}
        <Modal.Body>
          {modalLoading && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1050,
              }}
            >
              <div
                className="spinner-border text-light"
                role="status"
                aria-hidden="true"
              ></div>
              <span style={{ marginLeft: 12, color: "white" }}></span>
            </div>
          )}

          {/* Filter Panel for modal searches */}
          {showFilters && (
            <FilterPanel
              open={showFilters}
              sections={filterSections.length > 0 ? filterSections : undefined}
              initialValues={modalFiltersRaw}
              onClose={() => setShowFilters(false)}
              onApply={sel => {
                const apiFilters = {}
                if (sel) {
                  if (sel.price) apiFilters.price = sel.price
                  if (sel.rating) apiFilters.rating = sel.rating
                  if (sel.downloads) apiFilters.downloads = sel.downloads
                  if (sel.revenue) apiFilters.revenue = sel.revenue
                  if (sel.release_date) apiFilters.release_date = sel.release_date
                  if (sel.genre) apiFilters.genre = sel.genre
                  if (sel.category) apiFilters.category = sel.category;
                  if (sel.bundle) apiFilters.bundle = sel.bundle
                  if (sel.number_of_players)
                    apiFilters.number_of_players = sel.number_of_players
                  // Accept either `sort_by` (preferred) or legacy `sort` and normalize
                  if (sel.sort_by) apiFilters.sort_by = sel.sort_by
                  else if (sel.sort) apiFilters.sort_by = sel.sort
                }
                // persist both raw UI selection and the API-ready filters used for searches
                setModalFilters(apiFilters)
                setModalFiltersRaw(sel || {})
                // If a search term is present, run the modal search immediately with new filters
                if (localInput && localInput.trim()) {
                  setSubmittedKeyword(localInput.trim())
                }
                setShowFilters(false)
              }}
            />
          )}
          <div className="key_track_search" style={{ maxWidth: "100%" }}>
            <div className="key_track_search_inn">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="text"
                placeholder="Enter App Name"
                value={localInput}
                onChange={e => {
                  // Only update local input on typing. Do NOT call parent handlers here
                  // to avoid triggering parent searches while the user is typing in the modal.
                  setLocalInput(e.target.value)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (localInput && localInput.trim()) {
                      const term = localInput.trim()
                      // Sync term to parent before calling search
                      if (typeof onSearchChange === 'function') {
                        onSearchChange({ target: { value: term } })
                      }
                      setSubmittedKeyword(term)
                      // Always force fresh fetch, same as clicking Search, so that
                      // re-submitting the same keyword doesn't silently reuse a stale
                      // (or still-resolving-in-background) SWR cache entry.
                      swr.mutate()
                    }
                  }
                }}
              />
              <button
                onClick={() => {
                  // start modal-controlled search only when user explicitly clicks Search
                  const term = localInput.trim()
                  // Sync term to parent before calling search
                  if (typeof onSearchChange === 'function') {
                    onSearchChange({ target: { value: term } })
                  }
                  setSubmittedKeyword(term)

                  // Always force fresh fetch when user explicitly clicks Search button,
                  // regardless of whether the keyword is the same or different.
                  swr.mutate()
                }}
                disabled={modalLoading || isLoadingResults}
              >
                {/* show spinner inside the button while SWR is polling/loading */}
                {isLoadingResults && (
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ marginRight: 8 }}
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                Search
              </button>
              <button
                type="button"
                className="filter-trigger"
                onClick={() => setShowFilters(true)}
                aria-expanded={showFilters}
                style={{ marginLeft: 8 }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ marginRight: 8 }}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z"
                    fill="#fff"
                  />
                </svg>
                Filter
                {modalFiltersRaw && Object.keys(modalFiltersRaw).length > 0 && (
                  <span className="filter-badge">
                    {Object.values(modalFiltersRaw).reduce((acc, v) => {
                      if (v == null) return acc
                      if (Array.isArray(v)) return acc + v.length
                      if (typeof v === 'string' || typeof v === 'number') return acc + (String(v).trim() ? 1 : 0)
                      return acc + 1
                    }, 0)}
                  </span>
                )}
              </button>
              {/* Checkbox moved to platform-tabs area to align with the Meta tab */}
            </div>

            {/* Platform Tabs */}
            <div
              className="platform-tabs"
              style={{ marginTop: "20px", marginBottom: "15px" }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Nav pills style={{ marginLeft: 0 }}>
                  {!platformsLoading && getActivePlatforms().map((platform) => {
                    const isPlatformDisabled = disabledPlatformIds.includes(platform.id)
                    const navLink = (
                      <NavLink
                        style={{
                          cursor: isPlatformDisabled ? "not-allowed" : "pointer",
                          backgroundColor:
                            activeTab === platform.id ? "#f6255a" : "transparent",
                          color: isPlatformDisabled ? "#7a7f8c" : (activeTab === platform.id ? "#fff" : "inherit"),
                          borderColor:
                            activeTab === platform.id ? "#f6255a" : "transparent",
                          opacity: isPlatformDisabled ? 0.55 : 1,
                        }}
                        className={classnames({
                          active: activeTab === platform.id,
                        })}
                        aria-disabled={isPlatformDisabled}
                        onClick={() => {
                          if (isPlatformDisabled) return
                          handleTabChange(platform.id)
                        }}
                      >
                        <span className="d-block d-sm-block">
                          <span style={PLATFORM_LABEL_STYLE}>
                            <PlatformIcon platform={platform.id} size={getPlatformIconSize(platform.id)} />
                            {platform.display_name}
                          </span>
                        </span>
                      </NavLink>
                    )
                    return (
                      <NavItem key={platform.id}>
                        {isPlatformDisabled ? (
                          <span className="platform-tab-tooltip-wrap" data-tooltip={disabledPlatformTooltip}>
                            {navLink}
                          </span>
                        ) : navLink}
                      </NavItem>
                    )
                  })}
                  {platformsLoading && (
                    <NavItem>
                      <NavLink style={{ cursor: "default", color: "#999" }}>
                        <span className="d-block d-sm-block">Loading...</span>
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
                <div style={{ marginLeft: 12, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showTrackedOnly}
                      onChange={e => setShowTrackedOnly(!!e.target.checked)}
                      style={{ width: 16, height: 16 }}
                    />
                    <span style={{ fontSize: 13, userSelect: 'none' }}>Tracked Apps</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Applied Filter Badges */}
            {Object.keys(modalFiltersRaw || {}).length > 0 && (
              <div style={{ marginTop: '12px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(modalFiltersRaw).map(([key, value]) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) return null;
                  const displayValue = Array.isArray(value) ? value.join(', ') : value;
                  return (
                    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                      <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                      <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRemoveModalFilter(key);
                        }}
                        aria-label={`Remove filter ${key}`}
                        style={{
                          marginLeft: 4,
                          background: 'transparent',
                          border: 'none',
                          color: '#F6255A',
                          cursor: 'pointer',
                          fontSize: 12,
                          lineHeight: 0.9
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            <div className="grp_result_btm key_detail_modal">
              <h6>
                Search Result (
                {getPlatformDisplayName(activeTab)})
              </h6>
              {/* Bulk action bar for multi-selection - App Tracking only */}
              {enableBulkActions && selectedIds && selectedIds.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(246,37,90,0.08), rgba(255,154,184,0.06))',
                  border: '1px solid rgba(246,37,90,0.15)',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{ 
                    color: '#fff', 
                    fontSize: '15px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      background: '#F6255A',
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }}></div>
                    <span style={{ fontWeight: 600 }}>{selectedIds.length}</span>
                    <span style={{ opacity: 0.85 }}>app{selectedIds.length !== 1 ? 's' : ''} selected</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                      onClick={() => {
                        const allIds = filteredResults.map(game => game.item_id || game._id).filter(Boolean)
                        setSelectedIds(allIds)
                      }}
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        minHeight: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        minWidth: '120px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.08))'
                        e.target.style.borderColor = 'rgba(255,255,255,0.35)'
                        e.target.style.transform = 'translateY(-2px)'
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))'
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)'
                        e.target.style.transform = 'translateY(0px)'
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedIds([])}
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '8px 24px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        minHeight: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        minWidth: '120px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.08))'
                        e.target.style.borderColor = 'rgba(255,255,255,0.35)'
                        e.target.style.transform = 'translateY(-2px)'
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))'
                        e.target.style.borderColor = 'rgba(255,255,255,0.2)'
                        e.target.style.transform = 'translateY(0px)'
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      Clear
                    </button>
                    <button
                      onClick={async () => {
                        if (!selectedIds.length) return
                        setModalLoading(true)
                        try {
                          // Track each selected app sequentially to maintain order
                          const selectedGames = selectedIds.map(id => 
                            filteredResults.find(g => (g.item_id || g._id) === id)
                          ).filter(Boolean)
                          
                          // Filter out already tracked apps
                          const gamesToTrack = selectedGames.filter(game => {
                            const id = game.item_id || game._id
                            const isTracked = effectiveTrackedIdsForRender && effectiveTrackedIdsForRender.includes(id)
                            return !isTracked
                          })
                          
                          if (gamesToTrack.length === 0) {
                            setModalLoading(false)
                            return
                          }
                          
                          for (const game of gamesToTrack) {
                            const id = game.item_id || game._id
                            if (typeof onSelectApp === 'function') {
                              await Promise.resolve(onSelectApp(game))
                            } else {
                              const payload = { item_id: id }
                              await axios.post(`${api}/tracker/tracking/app/`, payload, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                              })
                            }
                            // Small delay to ensure proper ordering
                            await new Promise(resolve => setTimeout(resolve, 100))
                          }
                          // Clear selection after successful tracking
                          setSelectedIds([])
                          // Close modal if successful
                          if (typeof handleClose === 'function') handleClose()
                        } catch (err) {
                          console.error('bulk tracking failed', err)
                        } finally {
                          setModalLoading(false)
                        }
                      }}
                      disabled={modalLoading}
                      style={{
                        background: modalLoading 
                          ? 'linear-gradient(135deg, rgba(246,37,90,0.6), rgba(246,37,90,0.4))' 
                          : 'linear-gradient(135deg, #F6255A, #e11d4c)',
                        border: 'none',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '8px 20px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: modalLoading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minHeight: '36px',
                        minWidth: '140px',
                        justifyContent: 'center',
                        boxShadow: modalLoading 
                          ? 'none' 
                          : '0 4px 12px rgba(246,37,90,0.25)',
                        transform: modalLoading ? 'scale(0.98)' : 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        if (!modalLoading) {
                          e.target.style.background = 'linear-gradient(135deg, #e11d4c, #c9184a)'
                          e.target.style.transform = 'translateY(-1px) scale(1.02)'
                          e.target.style.boxShadow = '0 6px 16px rgba(246,37,90,0.35)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!modalLoading) {
                          e.target.style.background = 'linear-gradient(135deg, #F6255A, #e11d4c)'
                          e.target.style.transform = 'translateY(0px) scale(1)'
                          e.target.style.boxShadow = '0 4px 12px rgba(246,37,90,0.25)'
                        }
                      }}
                    >
                      {modalLoading && (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      )}
                      Add Selected ({selectedIds.length})
                    </button>
                  </div>
                </div>
              )}
              <div className="app_modal_scroll">
                {(isLoadingResults && (submittedKeyword || (searchTerm && String(searchTerm).trim()))) ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="grp_result_btm_box">
                      <div className="grp_result_btm_box_lt">
                        <div className="grp_result_btm_box_lt_img">
                          <Skeleton
                            width={96}
                            height={54}
                            borderRadius={8}
                            baseColor="#2a2a35"
                            highlightColor="#2a2a35"
                          />
                        </div>
                        <div className="app_detail_txt">
                          <h4>
                            <Skeleton
                              width={120}
                              baseColor="#2a2a35"
                              highlightColor="#2a2a35"
                            />
                          </h4>
                          <p>
                            <Skeleton
                              width={80}
                              baseColor="#2a2a35"
                              highlightColor="#2a2a35"
                            />
                          </p>
                        </div>
                      </div>
                      <div className="grp_result_btm_box_rt">
                        <Skeleton
                          width={60}
                          height={24}
                          baseColor="#2a2a35"
                          highlightColor="#2a2a35"
                        />
                      </div>
                    </div>
                  ))
                ) : filteredResults.length === 0 ? (
                  <div className="text-center my-2" style={{
                    background: "rgba(30, 31, 41, 0.4)",
                    borderRadius: "16px",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    padding: "48px 20px",
                    marginTop: "12px"
                  }}>
                    <div style={{
                      width: "56px",
                      height: "56px",
                      background: "rgba(246, 37, 90, 0.1)",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px"
                    }}>
                      <Search size={24} color="#F6255A" />
                    </div>
                    <h6 style={{ color: "#fff", fontWeight: 600, marginBottom: "8px", fontSize: "16px" }}>
                      {submittedKeyword ? "No Results Found" : "Search Apps & Games"}
                    </h6>
                    <p style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "13px", maxWidth: "280px", margin: "0 auto", lineHeight: "1.5" }}>
                      {submittedKeyword
                        ? (showTrackedOnly
                          ? `We couldn't find any tracked apps matching "${submittedKeyword}"`
                          : `We couldn't find any ${activeTab} results for "${submittedKeyword}"`)
                        : "Type a name to search and track your favorite apps across platforms."
                      }
                    </p>
                  </div>
                ) : (
                  filteredResults.map((game, index) => {
                    const id = game.item_id || game._id
                    const alreadyTracked = effectiveTrackedIdsForRender && effectiveTrackedIdsForRender.includes(id)
                    // Determine whether this item is already present in the requested
                    // create-tag (active group). If so, do not show the Add to Tag action.
                    const isInTargetTag = (() => {
                      if (!createTag || !Array.isArray(tags)) return false
                      const tagObj = tags.find(t => t && t.name === createTag)
                      return !!(tagObj && Array.isArray(tagObj.game_ids) && tagObj.game_ids.includes(id))
                    })()
                    return (
                      <div key={id || index} className="grp_result_btm_box" draggable onDragStart={e => handleDragStart(e, id)} onDragEnd={handleDragEnd}>
                        <div className="grp_result_btm_box_lt" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {/* Multi-select checkbox - App Tracking only */}
                          {enableBulkActions && (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelectId(id)}
                              disabled={alreadyTracked}
                              style={{
                                width: 18,
                                height: 18,
                                cursor: alreadyTracked ? 'not-allowed' : 'pointer',
                                accentColor: '#F6255A',
                                opacity: alreadyTracked ? 0.5 : 1
                              }}
                            />
                          )}
                          <div className="grp_result_btm_box_lt_img">
                            <img
                              style={{ width: 96, height: 54, borderRadius: 8 }}
                              src={game.image_url}
                              alt={game.name}
                            />

                          </div>
                          <div className="app_detail_txt">
                            <h4>{game.name}</h4>
                            <p>
                              <span style={PLATFORM_LABEL_STYLE}>
                                <PlatformIcon platform={game.platform} size={getPlatformIconSize(game.platform)} />
                                {getPlatformDisplayName(game.platform)}
                              </span>
                            </p>
                            {/* Show 'Already Tracked' badge below the name when applicable.
                              Also show the group(s) this item belongs to (if any) before the badge. */}
                            {alreadyTracked && (() => {
                              const groupsForItem = Array.isArray(tags)
                                ? tags.filter(t => Array.isArray(t.game_ids) && t.game_ids.includes(id)).map(t => t.name)
                                : []

                              return (
                                <div style={{ marginTop: 6 }}>
                                  {groupsForItem.length > 0 && (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                                      {groupsForItem.map((g, i) => (
                                        <a
                                          key={g + i}
                                          href="#"
                                          onClick={e => {
                                            e.preventDefault()
                                            try {
                                              // Close modal first
                                              if (typeof handleClose === 'function') handleClose()
                                            } catch (err) { }
                                            try {
                                              // Notify parent to open the group
                                              if (typeof onOpenGroup === 'function') onOpenGroup(g)
                                            } catch (err) { }
                                          }}
                                          style={{
                                            background: 'rgba(255,255,255,0.06)',
                                            color: '#fff',
                                            borderRadius: 12,
                                            fontSize: 11,
                                            padding: '6px 10px',
                                            lineHeight: '18px',
                                            textDecoration: 'none',
                                            display: 'inline-block',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          {g}
                                        </a>
                                      ))}
                                    </div>
                                  )}

                                  <div style={{ marginTop: 4 }}>
                                    <span style={{
                                      background: 'rgba(246,37,90,0.12)',
                                      color: '#fff',
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      padding: '6px 10px',
                                      display: 'inline-block'
                                    }}>Already Tracked</span>
                                  </div>
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                        <div className="grp_result_btm_box_rt" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {enableBulkActions && selectedIds.includes(id) && (
                            <span style={{
                              background: 'rgba(246,37,90,0.12)',
                              color: '#F6255A',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '4px 8px',
                              textTransform: 'uppercase'
                            }}>
                              Selected
                            </span>
                          )}
                          {alreadyTracked ? (
                            showTrackedSearchActions ? (
                              // In create-tag mode with an active search, show View + Remove-from-tag
                              <>
                                {inCreateMode ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    {!isInTargetTag && (
                                      <a
                                        href="#"
                                        onClick={async e => {
                                          e.preventDefault()
                                          if (!onAddToTag || rowLoadingMap[id]) return
                                          try {
                                            setRowLoading(id, true)
                                            await Promise.resolve(onAddToTag(id, createTag))
                                          } catch (err) {
                                            console.error('add to tag failed', err)
                                          } finally {
                                            setRowLoading(id, false)
                                          }
                                        }}
                                        style={{
                                          color: '#fff',
                                          textDecoration: 'underline',
                                          pointerEvents: rowLoadingMap[id] ? 'none' : 'auto',
                                          opacity: rowLoadingMap[id] ? 0.6 : 1,
                                          cursor: rowLoadingMap[id] ? 'not-allowed' : 'pointer',
                                        }}
                                      >
                                        {rowLoadingMap[id] ? (
                                          <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} role="status" aria-hidden="true"></span>
                                        ) : null}
                                        Add to Tag
                                      </a>
                                    )}
                                    {/* Move button removed per request */}
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <a
                                      href={`/xr/app/${(game.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${id}&openGraph=app_score`}
                                      style={{ color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                                      title="View"
                                      aria-label={`View ${game.name}`}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </a>
                                    <a
                                      href="#"
                                      onClick={e => { e.preventDefault(); setConfirmRemove({ itemId: id, tagName: null, itemName: game.name }) }}
                                      style={{ color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                                      title="Remove"
                                      aria-label={`Remove ${game.name}`}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                        <path d="M3 6h18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M10 11v6M14 11v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </a>
                                  </span>
                                )}

                              </>
                            ) : (
                              <>
                                {inCreateMode ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    {!isInTargetTag && (
                                      <a
                                        href="#"
                                        onClick={async e => {
                                          e.preventDefault()
                                          if (!onAddToTag || rowLoadingMap[id]) return
                                          try {
                                            setRowLoading(id, true)
                                            await Promise.resolve(onAddToTag(id, createTag))
                                          } catch (err) {
                                            console.error('add to tag failed', err)
                                          } finally {
                                            setRowLoading(id, false)
                                          }
                                        }}
                                        style={{
                                          color: '#fff',
                                          textDecoration: 'underline',
                                          marginRight: 8,
                                          pointerEvents: rowLoadingMap[id] ? 'none' : 'auto',
                                          opacity: rowLoadingMap[id] ? 0.6 : 1,
                                          cursor: rowLoadingMap[id] ? 'not-allowed' : 'pointer',
                                        }}
                                      >
                                        {rowLoadingMap[id] ? (
                                          <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} role="status" aria-hidden="true"></span>
                                        ) : null}
                                        Add to Tag
                                      </a>
                                    )}
                                    {/* Move button removed per request */}
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <a
                                      href={`/xr/app/${(game.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${id}&openGraph=app_score`}
                                      style={{ color: '#fff', textDecoration: 'none', marginRight: 8, display: 'inline-flex', alignItems: 'center' }}
                                      title="View"
                                      aria-label={`View ${game.name}`}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </a>
                                    <a
                                      href="#"
                                      onClick={e => { e.preventDefault(); setConfirmRemove({ itemId: id, tagName: null, itemName: game.name }) }}
                                      style={{ color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', marginLeft: 0 }}
                                      title="Remove"
                                      aria-label={`Remove ${game.name}`}
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                        <path d="M3 6h18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M10 11v6M14 11v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </a>
                                  </span>
                                )}
                                {/* Move/Add select intentionally hidden in generic select modal; show only in create-tag mode */}
                                {/* In generic select modal, show a single untrack button to remove app from tracking */}
                                {/* Untrack button removed */}
                              </>
                            )
                          ) : (
                            <>
                              {inCreateMode ? (
                                <a
                                  href="#"
                                  onClick={async e => {
                                    e.preventDefault()
                                    if (rowLoadingMap[id]) return
                                    try {
                                      await handleTrackClick(game, createTag, id)
                                    } catch (err) {
                                      // already logged in handler
                                    }
                                  }}
                                  style={{
                                    pointerEvents: rowLoadingMap[id] ? 'none' : (modalLoading ? "none" : "auto"),
                                    opacity: rowLoadingMap[id] || modalLoading ? 0.6 : 1,
                                    cursor: rowLoadingMap[id] || modalLoading ? "not-allowed" : "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  {rowLoadingMap[id] ? (
                                    <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} role="status" aria-hidden="true"></span>
                                  ) : null}
                                  {showSelectButton ? "Select" : "Track"}
                                </a>
                              ) : (
                                <a
                                  href="#"
                                  onClick={async e => {
                                    e.preventDefault()
                                    if (!modalLoading) {
                                      try {
                                        await handleTrackClick(game)
                                      } catch (err) {
                                        // already logged
                                      }
                                    }
                                  }}
                                  style={{
                                    pointerEvents: rowLoadingMap[id] ? 'none' : (modalLoading ? "none" : "auto"),
                                    opacity: rowLoadingMap[id] || modalLoading ? 0.6 : 1,
                                    cursor: rowLoadingMap[id] || modalLoading ? "not-allowed" : "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  {rowLoadingMap[id] ? (
                                    <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} role="status" aria-hidden="true"></span>
                                  ) : null}
                                  {showSelectButton ? "Select" : "Track"}
                                </a>
                              )}

                              {/* per-row Add to Tag dropdown removed */}
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </div>
          {/* Confirmation modal for removing an app from a tag */}
          {confirmRemove && (
            <ConfirmRemoveTagModal
              show={true}
              onClose={() => setConfirmRemove(null)}
              onConfirm={async ({ removeFromEverywhere }) => {
                if (!confirmRemove) return
                const { itemId, tagName } = confirmRemove
                setModalLoading(true)
                try {
                  const removeGlobal = removeFromEverywhere || !tagName

                  if (typeof onRemoveFromTag === 'function') {
                    await onRemoveFromTag(itemId, removeGlobal ? null : tagName)
                  } else {
                    // fallback: existing logic only handled global remove
                    await axios.delete(`${api}/tracker/tracking/remove_entity`, {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                      },
                      data: { item_id: itemId },
                    })
                  }
                  setConfirmRemove(null)
                } catch (err) {
                  console.error("confirm remove failed", err)
                  setConfirmRemove(null)
                } finally {
                  setModalLoading(false)
                }
              }}
              itemName={confirmRemove.itemName}
              tagName={confirmRemove.tagName}
            />
          )}
          {/* Move modal removed */}
          <style>{`
          /* Stronger rules to prevent other theme rules from turning this button pink */
          .filter-trigger{
            background: transparent !important;
            background-color: transparent !important;
            border: 1px solid rgba(255,255,255,0.12) !important;
            color: #fff !important;
            border-radius: 12px;
            padding: 8px 12px;
            display:inline-flex;
            align-items:center;
            gap:6px;
            height:44px;
            cursor:pointer;
            font-weight:600;
            appearance: none;
          }
          .filter-trigger svg{ opacity:0.95 }
          .filter-trigger:hover,
          .filter-trigger:focus,
          .filter-trigger:active{
            background: rgba(255,255,255,0.03) !important;
            color: #fff !important;
            border-color: rgba(255,255,255,0.18) !important;
          }
          /* Truncate long app names so action controls align */
          .app_detail_txt h4{
            max-width: 220px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
            margin: 0; /* remove bottom margin so badge sits directly under name */
          }
          .app_detail_txt p{ max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin: 0 }
          /* Member tag controls styling */
          .member-tag-select{ min-width: 120px; max-width: 220px; padding: 6px 8px; background: rgba(255,255,255,0.03); color: #fff; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; }
          .member-tag-btn{ display: inline-flex; align-items: center; margin-left: 4px; }
          .member-tag-badge{ display:inline-block; background: rgba(246,37,90,0.12); color: #fff; padding: 6px 10px; border-radius: 12px; min-width: 40px; max-width: 160px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; text-align: center; font-weight:600; }
          /* remove button styles removed */
        `}</style>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default KeywordTrackingDetailsModal
